import { message } from 'antd';

import { IUserAttributes } from '@lib/types/authUser.types';

import { US_ETHNICITY_MAP } from './constants';

// legacy - unmodified
export const validatePassword = (
  password: string,
  additionalChecks: { label: string; value: string }[] = [],
  badPasswords?: {
    [password: string]: boolean | { label: string; value: string };
  }
): boolean | { label: string; value: string } => {
  password = password.toLowerCase().trim();

  const BAD_PASSWORD_MAP = Object.assign({}, badPasswords);
  const pieces = [];

  let BAD_PASSWORD = null;

  for (const addtlValue of additionalChecks) {
    addtlValue.value = addtlValue.value?.toLowerCase().trim();
    if (addtlValue.value) {
      BAD_PASSWORD_MAP[addtlValue?.value] = addtlValue;
      pieces.push(addtlValue);

      const adjusted_value = addtlValue?.value?.replace(/\W/gi, '-');
      const splitValue = adjusted_value ? adjusted_value.split('-') : [];
      for (const sect of splitValue) {
        BAD_PASSWORD_MAP[sect] = addtlValue;
        BAD_PASSWORD_MAP[sect?.replace(/\d/gi, '')] = addtlValue;
        if (sect.length > 2) {
          pieces.push({ ...addtlValue, value: sect });
        }
        if (sect?.replace(/\d/gi, '').length > 2) {
          pieces.push({ ...addtlValue, value: sect?.replace(/\d/gi, '') });
        }
      }
    }
  }

  const passwordPieces = password?.replace(/\d/gi, '-')?.split('-');
  passwordPieces?.forEach((piece) => {
    const foundPiece = pieces.find(
      (subPiece) =>
        subPiece.value.trim().toLowerCase() == piece.trim().toLowerCase() ||
        piece.includes(subPiece.value.trim().toLowerCase())
    );
    if (foundPiece) {
      BAD_PASSWORD = foundPiece;
    }
  });

  const ADJ_PW = password?.replace(/\d/gi, '')?.replace(/\W/gi, '');

  if (BAD_PASSWORD_MAP[password]) {
    BAD_PASSWORD = BAD_PASSWORD_MAP[password];
  }

  if (BAD_PASSWORD_MAP[ADJ_PW]) {
    BAD_PASSWORD = BAD_PASSWORD_MAP[ADJ_PW];
  }

  return BAD_PASSWORD;
};

export const displayError = (ish: string): void => {
  message.error(ish.replace(/[\S]* failed with error/, ''), 3);
};

export const getAttributesFromURL = (url: string): IUserAttributes => {
  const queryString = url?.split('?')[1];
  const params = queryString?.split('&');
  return params?.reduce((prev, param) => {
    const [key, value] = param?.split('=');
    prev[key] = decodeURIComponent(value);
    return prev;
  }, {});
};

// stricter than parseInt()
export const parseNumericString = (input: string) => {
  // use parseInt with radix 10 to ensure decimal numbers are parsed correctly
  const parsedValue = parseInt(input, 10);

  // check if the entire string consists of numerical characters
  if (!isNaN(parsedValue) && String(parsedValue) === input) {
    return parsedValue;
  } else {
    return NaN;
  }
};

export const getStatus = (
  status: string | null,
  statuses: {
    text: string;
    abbrev: string;
    value: string;
    ukText?: string;
    ukAbbrev?: string;
  }[],
  isAbbrev: boolean,
  isUk = false
): string => {
  const { abbrev, ukAbbrev } = statuses.find(
    ({ value }) => value === status
  ) || {
    abbrev: '',
  };
  const { text, ukText } = statuses.find(({ value }) => value === status) || {
    text: '',
  };

  const finalAbbrev = isUk && ukAbbrev ? ukAbbrev : abbrev;
  const finalText = isUk && ukText ? ukText : text;
  return isAbbrev ? finalAbbrev : finalText;
};

export const formatEthnicityString = (ethnicity?: string) => {
  if (!ethnicity || typeof ethnicity !== 'string') return '';

  return US_ETHNICITY_MAP[ethnicity] || ethnicity;
};

const PASSWORD_VALIDATOR_CODES = {
  MIN_LENGTH_8: 'MIN_LENGTH_8',
  ONE_UPPER_CASE_LETTER: 'ONE_UPPER_CASE_LETTER',
  ONE_LOWER_CASE_LETTER: 'ONE_LOWER_CASE_LETTER',
  ONE_NUMBER: 'ONE_NUMBER',
  DOES_NOT_INCLUDE_PARTS_OF_WORDS: 'DOES_NOT_INCLUDE_PARTS_OF_WORDS',
  NOT_COMMON: 'NOT_COMMON',
};

type PasswordValidorInputType = {
  val: string | null | undefined;
  email?: string | null | undefined;
  commonPasswords?: { [password: string]: boolean } | undefined | null;
};

export const PasswordValidatorsConfig = {
  [PASSWORD_VALIDATOR_CODES.MIN_LENGTH_8]: {
    validate: ({ val }: PasswordValidorInputType) => val?.length >= 8,
    message: '8 characters minimum',
  },
  [PASSWORD_VALIDATOR_CODES.ONE_UPPER_CASE_LETTER]: {
    validate: ({ val }: PasswordValidorInputType) => val && /[A-Z]/.test(val),
    message: 'One uppercase letter',
  },
  [PASSWORD_VALIDATOR_CODES.ONE_LOWER_CASE_LETTER]: {
    validate: ({ val }: PasswordValidorInputType) => val && /[a-z]/.test(val),
    message: 'One lowercase letter',
  },
  [PASSWORD_VALIDATOR_CODES.ONE_NUMBER]: {
    validate: ({ val }: PasswordValidorInputType) => val && /\d/.test(val),
    message: 'One number',
  },
  [PASSWORD_VALIDATOR_CODES.DOES_NOT_INCLUDE_PARTS_OF_WORDS]: {
    validate: ({ val, email }: PasswordValidorInputType) => {
      if (!val) return false;
      if (!email) return true;

      const trimmedValLowercase = val?.trim()?.toLowerCase();
      const trimmedEmailLowercase = email?.trim()?.toLowerCase();
      if (trimmedValLowercase === trimmedEmailLowercase) return false;

      const emailAlphanumericPiecesToCheck = trimmedEmailLowercase
        .split(/[^a-zA-Z0-9]+/)
        .filter((piece) => piece?.length > 2);

      if (emailAlphanumericPiecesToCheck?.length) {
        const valIncludesEmailAlphanumericPiece =
          emailAlphanumericPiecesToCheck.some((piece) =>
            trimmedValLowercase.includes(piece)
          );
        if (valIncludesEmailAlphanumericPiece) return false;
      }

      const emailAlphabeticalPiecesToCheck = trimmedEmailLowercase
        .split(/[^a-zA-Z]+/)
        .filter((piece) => piece?.length > 2);

      if (emailAlphabeticalPiecesToCheck?.length) {
        const valIncludesEmailAlphabeticalPiece =
          emailAlphabeticalPiecesToCheck.some((piece) =>
            trimmedValLowercase.includes(piece)
          );
        if (valIncludesEmailAlphabeticalPiece) return false;
      }
      return true;
    },
    message: 'Does not include parts of name or email',
  },
  [PASSWORD_VALIDATOR_CODES.NOT_COMMON]: {
    validate: ({ val, commonPasswords }: PasswordValidorInputType) => {
      if (!val) return false;
      if (!commonPasswords) return false;

      const trimmedVal = val?.trim();

      const isValid =
        !commonPasswords[trimmedVal] &&
        !commonPasswords[trimmedVal.toLowerCase()] &&
        !commonPasswords[trimmedVal.toUpperCase()] &&
        !commonPasswords[val];
      return isValid;
    },
    message: 'Not a commonly used password (e.g. Password123)',
  },
};

export const PasswordValidators = Object.entries(PasswordValidatorsConfig).map(
  ([code, validator]) => ({
    code,
    ...validator,
  })
);
